/**
 * dayBookCache - the last good Day Book, remembered across unmounts (dbcache).
 *
 * Founder, 10 Sep 2026, on /portfolio: "when we click on office why does it take
 * time to show the data it keeps circling".
 *
 * The Day Book kept its whole read in component state, and `PortfolioCockpit`
 * renders it as `view === 'queue' && <PracticeQueue …>`. So every trip to
 * Office, and every hop to All work and back, threw the read away and put a
 * full-page "Building your work queue…" over `get_practice_queue()` again. That
 * query walks every client the caller can see, so its cost grew with the
 * practice: 210ms when it was written against 14 orgs, and the office is at 97
 * now. Nothing is wrong with the query. The screen simply forgot the answer it
 * already had, every single time.
 *
 * So the answer is held here, outside React, and the next mount paints it at
 * once and refreshes underneath, which is exactly what the Day Book's own 60
 * second background tick has always done. It is now applied to the first read
 * too.
 *
 * Three rules keep a cache from lying:
 *
 *  - **It is keyed on WHO is asking and WHICH office.** A different login, or a
 *    switched org, is a miss and gets the spinner it deserves. The Day Book is
 *    one practice's client list, and showing it under another name is worse
 *    than showing nothing.
 *  - **Nothing older than `DAY_BOOK_STALE_MS` is served.** Somebody acts on this
 *    screen, so past that point the honest answer is to go and read again.
 *  - **It holds only what the server said.** Nothing writes a derived or
 *    optimistic row in here, so a cached Day Book and a fresh one differ in
 *    their age and in nothing else.
 *
 * It is deliberately not a general query cache. One office, one snapshot, and no
 * eviction policy to get wrong.
 */
import type { PipelineIndex } from './chipWords';
import type { BoardCounts, FilingRow, PortalProblem } from './filingBoard';

/**
 * The stage layer that rides alongside the queue (fb-5). Held with the queue
 * rather than beside it, because a chip's words come from both, and a Day Book
 * painted from one without the other reads as work that has not started.
 */
export interface DayBookPipes {
    index: PipelineIndex;
    counts: Partial<BoardCounts>;
    clientIds: string[];
    rows: FilingRow[];
    /**
     * ptruth-3 — clients whose GSTIN the GST portal does not recognise, so Karr
     * has stopped creating their GST returns. Carried WITH the rows rather than
     * derived from them: these clients have no rows left, which is the whole
     * reason they have to be named somewhere.
     */
    portalProblems?: PortalProblem[];
}

export interface DayBookSnapshot<TQueue> {
    data: TQueue;
    pipes: DayBookPipes | null;
    /** `Date.now()` at the moment the server answered. */
    at: number;
}

/**
 * Twelve minutes. Long enough to cover a working session's worth of hopping
 * between the Office tabs and a client and back, short enough that a Day Book
 * left open over lunch is read again rather than believed.
 */
export const DAY_BOOK_STALE_MS = 12 * 60 * 1000;

let held: { key: string; snap: DayBookSnapshot<unknown> } | null = null;

/**
 * The cache key, or null when we do not yet know whose Day Book this is. A null
 * key never reads and never writes, because an unkeyed snapshot is exactly the
 * one that could be shown to the wrong person.
 */
export function dayBookKey(userId?: string | null, officeOrgId?: string | null): string | null {
    return userId && officeOrgId ? `${userId}|${officeOrgId}` : null;
}

/** The remembered Day Book for this key, or null if there is none or it is old. */
export function readDayBook<TQueue>(key: string | null, now = Date.now()): DayBookSnapshot<TQueue> | null {
    if (!key || !held || held.key !== key) return null;
    if (now - held.snap.at > DAY_BOOK_STALE_MS) { held = null; return null; }
    return held.snap as DayBookSnapshot<TQueue>;
}

/** Remember this read. One office at a time: a new key replaces the old one. */
export function writeDayBook<TQueue>(
    key: string | null, data: TQueue, pipes: DayBookPipes | null, now = Date.now(),
): void {
    if (!key) return;
    held = { key, snap: { data, pipes, at: now } };
}

/** Drop what is held. Sign-out, and the tests. */
export function forgetDayBook(): void {
    held = null;
}
